import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import Image, { getImageProps } from 'next/image';
import classNames from 'classnames';
import Layout from '@/components/Layout.js';
import { allSites } from '@/utils/domains.js';
import LeadFormLink from '@/components/LeadFormLink.js';
import { SiteFAQSection } from '@/components/SiteFAQAccordion.js';
import { TRANSFER_FAQS } from '@/utils/siteFaqs.js';
import { transferPartners } from '@/utils/transferPartners.js';
import TransferBanner from '@/public/images/transfer-banner.jpg';
import TransferBannerMobile from '@/public/images/transfer-banner-mobile.jpg';
import CampusGraduation from '@/public/images/campusgraduatio-banner.jpg';
import ActionSection from '@helios/ActionSection';
import Card from '@helios/Card';
import Container from '@helios/Container';
import Grid from '@helios/Grid';
import PageHero from '@helios/PageHero';
import Section from '@helios/Section';
import heroContentStyles from '@/styles/PageHeroContent.module.scss';
import styles from '@/styles/TransferPage.module.scss';

// The rendered widths (Phase 5 PR 5.4): the hero banner fills its half from
// 992px and the full width below; a partner logo fills its card (three
// across from 992px, two from 768px, one below, less the card padding); the
// institutions photo is 5/12 of the page from 992px.
const HERO_IMAGE_SIZES = '(max-width: 991px) 100vw, 50vw';
// The phone collage's optimised candidates for the hero's <source> (the
// SchedulePreference idiom): the same files next/image would serve it.
const {
  props: phoneHeroProps
} = getImageProps({
  src: TransferBannerMobile,
  alt: '',
  fill: true,
  sizes: '100vw'
});
const PARTNER_LOGO_SIZES = '(max-width: 767px) calc(100vw - 120px), (max-width: 991px) calc(50vw - 100px), (max-width: 1199px) calc(33vw - 96px), 380px';
const INSTITUTIONS_IMAGE_SIZES = '(max-width: 991px) 100vw, 42vw';
export const getStaticPaths = async () => {
  return {
    paths: allSites.map(site => ({
      params: {
        site
      }
    })),
    fallback: false
  };
};
export async function getStaticProps({
  params
}) {
  return {
    props: {
      site: params.site
    }
  };
}
function Transfer(t0) {
  const $ = _c(13);
  const {
    site
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Head><title>Transfer Partners | Campus</title><link rel="canonical" href="https://campus.edu/transfer" /><meta httpEquiv="X-UA-Compatible" content="IE=Edge" /><meta name="description" content="Plan your path to a bachelor's degree. Explore Campus Transfer Partners, see how credits apply, and transfer with greater confidence." /><meta name="viewport" content="width=device-width, initial-scale=1" /></Head>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PageHero.Content placement="split" tone="inverse"><h1 className={classNames(heroContentStyles.title, heroContentStyles["title--feature"], heroContentStyles["title--inverse"], styles["hero-title"])}>Built for What Comes Next</h1><p className={classNames(heroContentStyles.body, heroContentStyles["body--inverse"], styles["hero-body"])}>Campus has partnered with dozens of leading colleges and universities that share a strong commitment to helping students take the next step toward a bachelor's degree.</p></PageHero.Content>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <PageHero surface="dark"><PageHero.Container width="full"><PageHero.Split mediaFirstOnMobile={false}>{t2}<PageHero.Media placement="split"><div className={styles["hero-media"]}><picture><source media="(max-width: 991px)" sizes={phoneHeroProps.sizes} srcSet={phoneHeroProps.srcSet} /><Image alt="Built for What Comes Next" className={styles["hero-image"]} priority={true} sizes={HERO_IMAGE_SIZES} src={TransferBanner} /></picture></div></PageHero.Media></PageHero.Split></PageHero.Container></PageHero>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Section><Container><div className={styles.intro}><p className={styles.title}>Campus Transfer Partner Network</p><p className={styles.lead}>Through the Transfer Partner Network, students can see how their credits apply, what's required for transfer, and where their degree can lead from the moment they begin, reducing guesswork and avoiding surprises. This approach supports a faster, more cost-effective path forward and prepares students for continued education and career advancement.</p><p className={styles.lead}>Campus continues to expand the Transfer Partner Network to provide students with even more options for continuing their education.</p></div></Container></Section>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ActionSection layout="split" title={"Have\xA0questions? Talk to admissions."} actions={<LeadFormLink variant="solid">Learn more</LeadFormLink>}><p>Our admissions counselors are here to answer any questions and help you navigate the admissions process.</p></ActionSection>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className={classNames(styles.title, styles["partners-title"])}>Our partners</p>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      base: 1,
      md: 2,
      lg: 3
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Section><Container>{t6}<Grid columns={t7} gap="md">{transferPartners.map(_temp)}</Grid></Container></Section>;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className={styles["institutions-media"]}><Image alt="Campus Graduation" className={styles["institutions-image"]} sizes={INSTITUTIONS_IMAGE_SIZES} src={CampusGraduation} /></div>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <h2 className={styles["institutions-title"]}>For Colleges and Universities</h2>;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div id="wrapper">{t1}{t3}{t4}{t5}{t8}<Section padding="none" surface="subtle"><div className={styles.institutions}>{t9}<div className={styles["institutions-copy"]}>{t10}<p className={styles.lead}>Institutions committed to transfer-friendly degree pathways for upper-division, credit-ready students are invited to partner with Campus. Please email us at{" "}<a href="mailto:transferpartners@campus.edu">transferpartners@campus.edu</a>{" "}to get in touch.</p></div></div></Section><SiteFAQSection faqs={TRANSFER_FAQS} /></div>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== site) {
    t12 = <Layout site={site}>{t11}</Layout>;
    $[11] = site;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  return t12;
}
function _temp(partner) {
  return <Card className={styles["partner-card"]} fullHeight={true} key={partner.name} surface="subtle"><div className={styles["partner-logo"]}><Image alt={partner.name} className={styles["partner-logo-image"]} sizes={PARTNER_LOGO_SIZES} src={partner.image} /></div><p className={styles["partner-name"]}>{partner.name}</p></Card>;
}
export default Transfer;